import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Tag, Input, Tooltip, Select } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updatePermission, updateFormAction } from "../../../redux/permissions/permissionsSlice";
import {
	useLazyGetPermissionsQuery,
	usePostPermissionMutation,
	usePutPermissionMutation,
} from "../../../redux/permissions/permissionsAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import PermissionsTableActions from "./PermissionsTableActions";
import PermissionsHeader from "./PermissionsHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { PermissionInterface } from "../../../interfaces/Permissions.interface";

function PermissionsTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const { Option } = Select;
	const METHODS = ["GET", "POST", "PUT", "DELETE"];

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [permission, setPermission] = useState<PermissionInterface>({
		id: "",
		name: "",
		method: "GET",
		uri: "",
		group: "",
		parent: "",
	});
	const [permissions, setPermissions] = useState<PermissionInterface[]>([]);
	const [errorMessage, setErrorMessage] = useState<any>({});
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetPermissions,
		{
			data: dataGetPermissions,
			isLoading: isLoadingGetPermissions,
			isFetching: isFetchingGetPermissions,
			isError: isErrorGetPermissions,
			error: errorGetPermissions,
		},
	] = useLazyGetPermissionsQuery();

	const [
		triggerPostPermission,
		{
			isSuccess: isSuccessPostPermission,
			isLoading: isLoadingPostPermission,
			isError: isErrorPostPermission,
			error: errorPostPermission,
			reset: resetPostPermission,
		},
	] = usePostPermissionMutation();

	const [
		triggerPutPermission,
		{
			isSuccess: isSuccessPutPermission,
			isLoading: isLoadingPutPermission,
			isError: isErrorPutPermission,
			error: errorPutPermission,
			reset: resetPutPermission,
		},
	] = usePutPermissionMutation();

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { formAction, permission: selectedPermission } = useSelector((state: RootState) => state.permission);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getData = async (page: number, limit: number) => {
		const token = await GETJwtToken();
		const BODY: any = {
			page,
			limit,
			token,
		};
		triggerGetPermissions(BODY);
	};

	const onChangeFormValue = (newValue: string, variableName: string) => {
		const copy: PermissionInterface = structuredClone(permission);
		copy[variableName] = newValue;
		setPermission(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		const copy = structuredClone(permission);
		copy.parent = copy.parent.toUpperCase();
		copy.group = copy.group.toUpperCase();

		if (formAction === "create") {
			const BODY = {
				token,
				body: copy,
			};
			triggerPostPermission(BODY);
		} else {
			const BODY = {
				token,
				id: copy.id,
				body: copy,
			};
			triggerPutPermission(BODY);
		}
	};

	const resetForm = () => {
		resetPostPermission();
		resetPutPermission();
		setPermission({
			id: "",
			name: "",
			method: "GET",
			uri: "",
			group: "",
			parent: "",
		});
		setErrorMessage({});
		dispatch(updateFormAction("create"));
		dispatch(
			updatePermission({
				id: "",
				name: "",
				method: "GET",
				uri: "",
				group: "",
				parent: "",
			})
		);
	};

	const checkFormIsValid = () => {
		if (permission.name === "" || permission.uri === "" || permission.type === "") {
			return true;
		}
		return false;
	};

	const onChangeMethod = (newValue: any) => {
		const copy: PermissionInterface = structuredClone(permission);
		copy.method = newValue;
		setPermission(copy);
	};

	const handleTableChange = (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
		getData(pagination_.current, pagination_.pageSize);
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		getData(1, 10);
	}, []);

	useEffect(() => {
		if (dataGetPermissions && dataGetPermissions.data) {
			setPagination({
				...pagination,
				total: dataGetPermissions.total || 1,
			});
			setPermissions(dataGetPermissions.data);
		}
	}, [dataGetPermissions]);

	useEffect(() => {
		if (errorGetPermissions) {
			setErrorMessage(errorGetPermissions);
			setPermissions([]);
		}
	}, [errorGetPermissions]);

	useEffect(() => {
		resetForm();
	}, [isSuccessPostPermission, isSuccessPutPermission, isErrorPostPermission, isErrorPutPermission]);

	useEffect(() => {
		if (formAction === "create") {
			setPermission({
				id: "",
				name: "",
				method: "GET",
				uri: "",
				group: "",
				parent: "",
			});
		}
	}, [formAction]);

	useEffect(() => {
		if (formAction === "create") {
			setPermission({
				id: "",
				name: "",
				method: "GET",
				uri: "",
				group: "",
				parent: "",
			});
		}
	}, [formAction]);

	useEffect(() => {
		setPermission(selectedPermission);
	}, [selectedPermission]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div>
				<>
					<PermissionsHeader />
					<Table
						locale={{
							emptyText: (
								<TableNoDataBox
									isError={isErrorGetPermissions}
									errorMessage={
										errorMessage && errorMessage.data && errorMessage.data.label ? errorMessage.data.label : "Error"
									}
									noItemsFound='No permissions found'
								/>
							),
						}}
						rowClassName={(record, index) => getTableRowClass(index, theme)}
						dataSource={permissions}
						pagination={pagination}
						onChange={handleTableChange}
						size='small'
						className='mt-3'
						loading={
							isLoadingGetPermissions || isLoadingPostPermission || isLoadingPutPermission || isFetchingGetPermissions
						}
						footer={() => (
							<div className='generalStyles__flexEnd'>
								<Tag>{pagination.total} Rows</Tag>
							</div>
						)}
					>
						{/** PARENT */}
						<ColumnGroup title={t("general.module")}>
							<Column
								width={300}
								title={
									<Input
										size='small'
										placeholder={t("general.module")}
										value={formAction === "create" ? permission.parent : ""}
										disabled={formAction === "edit"}
										onChange={(event: React.FormEvent<HTMLInputElement>) => {
											onChangeFormValue(event.currentTarget.value, "parent");
										}}
									/>
								}
								dataIndex='parent'
								key='parent'
								render={(text, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<Input
												size='small'
												placeholder={t("general.parent")}
												value={permission.parent}
												onChange={(event: React.FormEvent<HTMLInputElement>) => {
													onChangeFormValue(event.currentTarget.value, "parent");
												}}
											/>
										) : (
											<div>{text}</div>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** GROUP */}
						<ColumnGroup title={t("general.group")}>
							<Column
								width={300}
								title={
									<Input
										size='small'
										placeholder={t("general.group")}
										value={formAction === "create" ? permission.group : ""}
										disabled={formAction === "edit"}
										onChange={(event: React.FormEvent<HTMLInputElement>) => {
											onChangeFormValue(event.currentTarget.value, "group");
										}}
									/>
								}
								dataIndex='group'
								key='group'
								render={(text, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<Input
												size='small'
												placeholder={t("general.group")}
												value={permission.group}
												onChange={(event: React.FormEvent<HTMLInputElement>) => {
													onChangeFormValue(event.currentTarget.value, "group");
												}}
											/>
										) : (
											<div>{text}</div>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** NAME */}
						<ColumnGroup title={t("general.name")}>
							<Column
								title={
									<Input
										size='small'
										placeholder={t("general.name")}
										value={formAction === "create" ? permission.name : ""}
										disabled={formAction === "edit"}
										onChange={(event: React.FormEvent<HTMLInputElement>) => {
											onChangeFormValue(event.currentTarget.value, "name");
										}}
									/>
								}
								dataIndex='name'
								key='name'
								render={(text, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<Input
												size='small'
												placeholder={t("general.name")}
												value={permission.name}
												onChange={(event: React.FormEvent<HTMLInputElement>) => {
													onChangeFormValue(event.currentTarget.value, "name");
												}}
											/>
										) : (
											<div>{text}</div>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** URI */}
						<ColumnGroup title='URI'>
							<Column
								title={
									<Input
										size='small'
										placeholder='URI'
										value={formAction === "create" ? permission.uri : ""}
										disabled={formAction === "edit"}
										onChange={(event: React.FormEvent<HTMLInputElement>) => {
											onChangeFormValue(event.currentTarget.value, "uri");
										}}
									/>
								}
								dataIndex='uri'
								key='uri'
								render={(text, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<Input
												size='small'
												placeholder='URI'
												value={permission.uri}
												onChange={(event: React.FormEvent<HTMLInputElement>) => {
													onChangeFormValue(event.currentTarget.value, "uri");
												}}
											/>
										) : (
											<div>{text}</div>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** METHOD */}
						<ColumnGroup title='Method'>
							<Column
								title={
									<Select
										showSearch
										optionFilterProp='children'
										allowClear
										size='small'
										value={permission.method}
										onChange={onChangeMethod}
										disabled={formAction === "edit"}
										className='generalStyles__width100'
									>
										{METHODS.map((item: string) => (
											<Option key={item} value={item}>
												{item}
											</Option>
										))}
									</Select>
								}
								dataIndex='method'
								key='method'
								render={(text: string, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<Select
												showSearch
												optionFilterProp='children'
												allowClear
												size='small'
												value={permission.method}
												onChange={onChangeMethod}
												className='generalStyles__width100'
											>
												{METHODS.map((item: string) => (
													<Option key={item} value={item}>
														{item}
													</Option>
												))}
											</Select>
										) : (
											<div>
												{text === "GET" && (
													<Tag className='generalStyles__width100px generalStyles__textCenter' color='green'>
														{text}
													</Tag>
												)}
												{text === "POST" && (
													<Tag className='generalStyles__width100px generalStyles__textCenter' color='yellow'>
														{text}
													</Tag>
												)}
												{text === "PUT" && (
													<Tag className='generalStyles__width100px generalStyles__textCenter' color='blue'>
														{text}
													</Tag>
												)}
												{text === "DELETE" && (
													<Tag className='generalStyles__width100px generalStyles__textCenter' color='red'>
														{text}
													</Tag>
												)}
											</div>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** ACTIONS */}
						<ColumnGroup align='center' title={t("edgeManagement.banks.actions")}>
							<Column
								align='center'
								title={
									<div className='generalStyles__flexCenter'>
										<div>
											<Tooltip title={t("general.save")}>
												<Button
													className={formAction === "create" && !checkFormIsValid() ? "buttonStyle__10" : ""}
													disabled={checkFormIsValid() || formAction === "edit"}
													onClick={onClickSave}
													icon={<FontAwesomeIcon icon={faSave} />}
												/>
											</Tooltip>
										</div>
										<div className='generalStyles__mlFix'>
											<Tooltip title={t("general.clear")}>
												<Button
													className={formAction === "create" ? "buttonStyle__14" : ""}
													onClick={resetForm}
													disabled={formAction === "edit"}
													icon={<FontAwesomeIcon icon={faX} />}
												/>
											</Tooltip>
										</div>
									</div>
								}
								key='action'
								render={(_: any, record: PermissionInterface) => (
									<div>
										{formAction === "edit" && record.id === permission.id ? (
											<div className='generalStyles__flexCenter'>
												<div>
													<Tooltip title={t("general.save")}>
														<Button
															className='buttonStyle__10'
															disabled={checkFormIsValid()}
															onClick={onClickSave}
															icon={<FontAwesomeIcon icon={faSave} />}
														/>
													</Tooltip>
												</div>
												<div className='generalStyles__mlFix'>
													<Tooltip title={t("general.cancel")}>
														<Button
															className='buttonStyle__14'
															onClick={resetForm}
															icon={<FontAwesomeIcon icon={faX} />}
														/>
													</Tooltip>
												</div>
											</div>
										) : (
											<PermissionsTableActions selectedItem={record} />
										)}
									</div>
								)}
							/>
						</ColumnGroup>
					</Table>
				</>
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostPermission}
				isSuccess={isSuccessPostPermission}
				requestType='POST'
				error={errorPostPermission}
				name='Permission'
			/>
			<GlobalAlert2
				isError={isErrorPutPermission}
				isSuccess={isSuccessPutPermission}
				requestType='PUT'
				error={errorPutPermission}
				name='Permission'
			/>
		</div>
	);
}

export default PermissionsTable;
